// src/components/Events/MediaRegistration.jsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import './MediaRegistration.css';

const MediaRegistration = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    pressId: null,
    assignmentLetter: null,
    invitationLetter: null
  });
  
  // File input refs
  const pressIdInputRef = useRef(null);
  const assignmentLetterInputRef = useRef(null);
  const invitationLetterInputRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    role: '',
    nationality: '',
    countryOfResidence: '',
    phone: '',
    email: '',
    organisationName: '',
    organisationType: '',
    countryOfRegistration: '',
    organisationWebsite: '',
    editorName: '',
    editorEmail: '',
    editorPhone: '',
    categoryRequested: '',
    coverageFormat: '',
    specificallyInvited: '',
    invitationReference: '',
    assignmentEditor: '',
    coveragePlan: '',
    totalCrew: '',
    additionalCrewNames: '',
    keyEquipment: '',
    specialTechnicalNeeds: '',
    sessionsRequired: '',
    travellingFrom: '',
    passportNationality: '',
    passportNumber: '',
    requiresVisaSupport: '',
    emergencyName: '',
    emergencyRelationship: '',
    emergencyPhone: '',
    pressIdUploaded: false,
    assignmentLetterUploaded: false,
    invitationLetterUploaded: false,
    declarationSignature: '',
    declarationDate: ''
  });

  // ===== FILE UPLOAD FUNCTIONS =====
  const uploadFile = async (file, fileType) => {
    if (!file) return null;

    try {
      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));

      const fileName = `${Date.now()}_${fileType}_${file.name}`;
      const filePath = `media_registrations/${fileName}`;

      const { error } = await supabase.storage
        .from('media-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        alert(`Failed to upload ${fileType}: ${error.message}`);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('media-documents')
        .getPublicUrl(filePath);

      setUploadedFiles(prev => ({ 
        ...prev, 
        [fileType]: { 
          name: file.name, 
          url: urlData.publicUrl,
          size: file.size,
          type: file.type
        } 
      }));

      return urlData.publicUrl;

    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload ${fileType}: ${error.message}`);
      return null;
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handlePressIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'pressId');
    if (url) {
      setFormData(prev => ({ ...prev, pressIdUploaded: true }));
    }
    e.target.value = '';
  };

  const handleAssignmentLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'assignmentLetter');
    if (url) {
      setFormData(prev => ({ ...prev, assignmentLetterUploaded: true }));
    }
    e.target.value = '';
  };

  const handleInvitationLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadFile(file, 'invitationLetter');
    if (url) {
      setFormData(prev => ({ ...prev, invitationLetterUploaded: true }));
    }
    e.target.value = '';
  };

  const getUploadStatus = (fileType) => {
    if (uploadingFiles[fileType]) {
      return <span className="upload-status uploading">⏳ Uploading...</span>;
    }
    if (uploadedFiles[fileType]) {
      return <span className="upload-status success">✅ {uploadedFiles[fileType].name}</span>;
    }
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }
    if (!formData.role.trim()) {
      errors.role = 'Role is required';
      isValid = false;
    }
    if (!formData.nationality.trim()) {
      errors.nationality = 'Nationality is required';
      isValid = false;
    }
    if (!formData.countryOfResidence.trim()) {
      errors.countryOfResidence = 'Country of residence is required';
      isValid = false;
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.organisationName.trim()) {
      errors.organisationName = 'Organisation name is required';
      isValid = false;
    }
    if (!formData.organisationType) {
      errors.organisationType = 'Organisation type is required';
      isValid = false;
    }
    if (!formData.editorName.trim()) {
      errors.editorName = 'Editor name is required';
      isValid = false;
    }
    if (!formData.editorEmail.trim()) {
      errors.editorEmail = 'Editor email is required';
      isValid = false;
    }
    if (!formData.categoryRequested) {
      errors.categoryRequested = 'Category is required';
      isValid = false;
    }
    if (!formData.coverageFormat) {
      errors.coverageFormat = 'Coverage format is required';
      isValid = false;
    }
    if (!formData.coveragePlan.trim()) {
      errors.coveragePlan = 'Coverage plan is required';
      isValid = false;
    }
    if (!formData.totalCrew) {
      errors.totalCrew = 'Total crew number is required';
      isValid = false;
    }
    if (!formData.emergencyName.trim()) {
      errors.emergencyName = 'Emergency contact name is required';
      isValid = false;
    }
    if (!formData.emergencyPhone.trim()) {
      errors.emergencyPhone = 'Emergency contact phone is required';
      isValid = false;
    }
    if (!formData.declarationSignature.trim()) {
      errors.declarationSignature = 'Signature is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstErrorField = document.querySelector('.input-error');
      if (firstErrorField) {
        firstErrorField.focus();
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const pressIdUrl = uploadedFiles.pressId?.url || null;
      const assignmentLetterUrl = uploadedFiles.assignmentLetter?.url || null;
      const invitationLetterUrl = uploadedFiles.invitationLetter?.url || null;

      const { error } = await supabase
        .from('media_registrations')
        .insert([{
          full_name: formData.fullName,
          role: formData.role,
          nationality: formData.nationality,
          country_of_residence: formData.countryOfResidence,
          phone: formData.phone,
          email: formData.email,
          organisation_name: formData.organisationName,
          organisation_type: formData.organisationType,
          country_of_registration: formData.countryOfRegistration,
          organisation_website: formData.organisationWebsite,
          editor_name: formData.editorName,
          editor_email: formData.editorEmail,
          editor_phone: formData.editorPhone,
          category_requested: formData.categoryRequested,
          coverage_format: formData.coverageFormat,
          specifically_invited: formData.specificallyInvited,
          invitation_reference: formData.invitationReference,
          assignment_editor: formData.assignmentEditor,
          coverage_plan: formData.coveragePlan,
          total_crew: formData.totalCrew,
          additional_crew_names: formData.additionalCrewNames,
          key_equipment: formData.keyEquipment,
          special_technical_needs: formData.specialTechnicalNeeds,
          sessions_required: formData.sessionsRequired,
          travelling_from: formData.travellingFrom,
          passport_nationality: formData.passportNationality,
          passport_number: formData.passportNumber,
          requires_visa_support: formData.requiresVisaSupport,
          emergency_name: formData.emergencyName,
          emergency_relationship: formData.emergencyRelationship,
          emergency_phone: formData.emergencyPhone,
          press_id_uploaded: formData.pressIdUploaded,
          assignment_letter_uploaded: formData.assignmentLetterUploaded,
          invitation_letter_uploaded: formData.invitationLetterUploaded,
          press_id_url: pressIdUrl,
          assignment_letter_url: assignmentLetterUrl,
          invitation_letter_url: invitationLetterUrl,
          declaration_signature: formData.declarationSignature,
          declaration_date: formData.declarationDate || new Date().toISOString().split('T')[0],
          status: 'pending'
        }]);

      if (error) throw error;

      setSubmitSuccess(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (fieldName) => {
    if (formErrors[fieldName]) {
      return <span className="field-error">{formErrors[fieldName]}</span>;
    }
    return null;
  };

  if (submitSuccess) {
    return (
      <div className="media-registration-page">
        <div className="media-registration-container success-container">
          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h2>Media Registration Submitted!</h2>
            <p>Thank you for registering for media accreditation at AICIS 2026.</p>
            <p>Your application has been received and will be reviewed by the accreditation committee.</p>
            <p className="success-detail">📧 You will receive a confirmation email at {formData.email}</p>
            <p className="success-note">
              <strong>Note:</strong> Submission does not guarantee accreditation. 
              Only media representatives whose applications have been formally approved will be issued 
              an AICIS 2026 Media Accreditation Pass.
            </p>
            <div className="success-actions">
              <Link to="/events/global-summit" className="btn btn-primary">
                ← Back to Global Summit
              </Link>
              <Link to="/" className="btn btn-secondary">
                Go to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="media-registration-page">
      <div className="media-registration-container">
        <div className="media-registration-header">
          <div className="header-brand">
            <span className="header-badge">AICIS 2026</span>
            <h1>Media Registration</h1>
            <h2>Global Summit – Media Accreditation</h2>
            <p className="header-subtitle">25–28 November 2026 | Abuja, Nigeria</p>
            <div className="header-note">
              <span className="note-icon">📋</span>
              <span>Submission does not guarantee accreditation. Limited to 4 complimentary crew spots.</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="media-registration-form">
          {/* SECTION 1 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">1</span>
              <h3>Applicant Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="fullName" placeholder="Enter your full name" value={formData.fullName} onChange={handleInputChange} className={formErrors.fullName ? 'input-error' : ''} />
                {renderError('fullName')}
              </div>
              <div className="form-group">
                <label>Role *</label>
                <input type="text" name="role" placeholder="e.g., Journalist, Photographer, Videographer" value={formData.role} onChange={handleInputChange} className={formErrors.role ? 'input-error' : ''} />
                {renderError('role')}
              </div>
              <div className="form-group">
                <label>Nationality *</label>
                <input type="text" name="nationality" placeholder="Enter your nationality" value={formData.nationality} onChange={handleInputChange} className={formErrors.nationality ? 'input-error' : ''} />
                {renderError('nationality')}
              </div>
              <div className="form-group">
                <label>Country of Residence *</label>
                <input type="text" name="countryOfResidence" placeholder="Enter your country of residence" value={formData.countryOfResidence} onChange={handleInputChange} className={formErrors.countryOfResidence ? 'input-error' : ''} />
                {renderError('countryOfResidence')}
              </div>
              <div className="form-group">
                <label>Phone (WhatsApp) *</label>
                <input type="tel" name="phone" placeholder="Enter your phone number" value={formData.phone} onChange={handleInputChange} className={formErrors.phone ? 'input-error' : ''} />
                {renderError('phone')}
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" placeholder="Enter your email address" value={formData.email} onChange={handleInputChange} className={formErrors.email ? 'input-error' : ''} />
                {renderError('email')}
              </div>
            </div>
          </section>

          {/* SECTION 2 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">2</span>
              <h3>Media Organisation</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Organisation Name *</label>
                <input type="text" name="organisationName" placeholder="Enter organisation name" value={formData.organisationName} onChange={handleInputChange} className={formErrors.organisationName ? 'input-error' : ''} />
                {renderError('organisationName')}
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select name="organisationType" value={formData.organisationType} onChange={handleInputChange} className={formErrors.organisationType ? 'input-error' : ''}>
                  <option value="">Select organisation type</option>
                  <option value="International TV">International TV</option>
                  <option value="International Radio">International Radio</option>
                  <option value="International Online">International Online</option>
                  <option value="African Digital">African Digital</option>
                  <option value="African TV">African TV</option>
                  <option value="African Radio">African Radio</option>
                  <option value="Nigerian Newspaper">Nigerian Newspaper</option>
                  <option value="Nigerian TV">Nigerian TV</option>
                  <option value="Nigerian Radio">Nigerian Radio</option>
                  <option value="Nigerian Online">Nigerian Online</option>
                  <option value="Photo Agency">Photo Agency</option>
                  <option value="News Agency">News Agency</option>
                  <option value="Other">Other</option>
                </select>
                {renderError('organisationType')}
              </div>
              <div className="form-group">
                <label>Country of Registration</label>
                <input type="text" name="countryOfRegistration" placeholder="Enter country of registration" value={formData.countryOfRegistration} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Organisation Website</label>
                <input type="url" name="organisationWebsite" placeholder="Enter organisation website" value={formData.organisationWebsite} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Editor/Authorising Officer Name *</label>
                <input type="text" name="editorName" placeholder="Enter editor/authorising officer name" value={formData.editorName} onChange={handleInputChange} className={formErrors.editorName ? 'input-error' : ''} />
                {renderError('editorName')}
              </div>
              <div className="form-group">
                <label>Editor/Authorising Officer Email *</label>
                <input type="email" name="editorEmail" placeholder="Enter editor/authorising officer email" value={formData.editorEmail} onChange={handleInputChange} className={formErrors.editorEmail ? 'input-error' : ''} />
                {renderError('editorEmail')}
              </div>
              <div className="form-group">
                <label>Editor/Authorising Officer Phone</label>
                <input type="tel" name="editorPhone" placeholder="Enter editor/authorising officer phone" value={formData.editorPhone} onChange={handleInputChange} />
              </div>
            </div>
          </section>

          {/* SECTION 3 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">3</span>
              <h3>Accreditation & Coverage</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Category Requested *</label>
                <select name="categoryRequested" value={formData.categoryRequested} onChange={handleInputChange} className={formErrors.categoryRequested ? 'input-error' : ''}>
                  <option value="">Select category</option>
                  <option value="International">International</option>
                  <option value="African Regional">African Regional</option>
                  <option value="Nigerian National">Nigerian National</option>
                </select>
                {renderError('categoryRequested')}
              </div>
              <div className="form-group">
                <label>Coverage Format *</label>
                <select name="coverageFormat" value={formData.coverageFormat} onChange={handleInputChange} className={formErrors.coverageFormat ? 'input-error' : ''}>
                  <option value="">Select coverage format</option>
                  <option value="TV">TV</option>
                  <option value="Radio">Radio</option>
                  <option value="Online">Online</option>
                  <option value="Photo">Photo</option>
                  <option value="Print">Print</option>
                  <option value="Digital">Digital</option>
                  <option value="Live Streaming">Live Streaming</option>
                  <option value="Multi-Platform">Multi-Platform</option>
                </select>
                {renderError('coverageFormat')}
              </div>
              <div className="form-group full-width">
                <label>Were you specifically invited by AICIS?</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input type="radio" name="specificallyInvited" value="Yes" checked={formData.specificallyInvited === 'Yes'} onChange={handleInputChange} />
                    <span>Yes</span>
                  </label>
                  <label className="radio-option">
                    <input type="radio" name="specificallyInvited" value="No" checked={formData.specificallyInvited === 'No'} onChange={handleInputChange} />
                    <span>No</span>
                  </label>
                </div>
              </div>
              {formData.specificallyInvited === 'Yes' && (
                <div className="form-group">
                  <label>Invitation Reference</label>
                  <input type="text" name="invitationReference" placeholder="Enter invitation reference or upload invite below" value={formData.invitationReference} onChange={handleInputChange} />
                </div>
              )}
            </div>
          </section>

          {/* SECTION 4 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">4</span>
              <h3>Assignment Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Assignment Editor/Producer</label>
                <input type="text" name="assignmentEditor" placeholder="Enter assignment editor/producer name" value={formData.assignmentEditor} onChange={handleInputChange} />
              </div>
              <div className="form-group full-width">
                <label>Coverage Plan *</label>
                <textarea name="coveragePlan" placeholder="Briefly describe your story angle/outputs" value={formData.coveragePlan} onChange={handleInputChange} rows={4} className={formErrors.coveragePlan ? 'input-error' : ''} />
                {renderError('coveragePlan')}
              </div>
            </div>
          </section>

          {/* SECTION 5 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">5</span>
              <h3>Crew & Equipment</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Total Crew Number *</label>
                <input type="number" name="totalCrew" placeholder="Enter total crew number" value={formData.totalCrew} onChange={handleInputChange} min="1" max="10" className={formErrors.totalCrew ? 'input-error' : ''} />
                {renderError('totalCrew')}
              </div>
              <div className="form-group">
                <label>Names of Additional Crew</label>
                <input type="text" name="additionalCrewNames" placeholder="Enter names of additional crew" value={formData.additionalCrewNames} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Key Equipment</label>
                <input type="text" name="keyEquipment" placeholder="e.g., Camera, Audio, Live Streaming" value={formData.keyEquipment} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Special Technical Needs</label>
                <input type="text" name="specialTechnicalNeeds" placeholder="e.g., broadcast position, live streaming" value={formData.specialTechnicalNeeds} onChange={handleInputChange} />
              </div>
            </div>
          </section>

          {/* SECTION 6 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">6</span>
              <h3>Access Required</h3>
            </div>
            <div className="form-group full-width">
              <label>Sessions Required</label>
              <input type="text" name="sessionsRequired" placeholder="e.g., Plenary, Ministerial, Exhibition, Media Centre" value={formData.sessionsRequired} onChange={handleInputChange} />
            </div>
          </section>

          {/* SECTION 7 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">7</span>
              <h3>International Media (If applicable)</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Travelling from (Country of Departure)</label>
                <input type="text" name="travellingFrom" placeholder="Enter country of departure" value={formData.travellingFrom} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Passport Nationality</label>
                <input type="text" name="passportNationality" placeholder="Enter passport nationality" value={formData.passportNationality} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Passport Number</label>
                <input type="text" name="passportNumber" placeholder="Enter passport number" value={formData.passportNumber} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Do you require an AICIS support letter for a visa?</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input type="radio" name="requiresVisaSupport" value="Yes" checked={formData.requiresVisaSupport === 'Yes'} onChange={handleInputChange} />
                    <span>Yes</span>
                  </label>
                  <label className="radio-option">
                    <input type="radio" name="requiresVisaSupport" value="No" checked={formData.requiresVisaSupport === 'No'} onChange={handleInputChange} />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 8 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">8</span>
              <h3>Emergency Contact</h3>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="emergencyName" placeholder="Enter emergency contact name" value={formData.emergencyName} onChange={handleInputChange} className={formErrors.emergencyName ? 'input-error' : ''} />
                {renderError('emergencyName')}
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <input type="text" name="emergencyRelationship" placeholder="Enter relationship" value={formData.emergencyRelationship} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" name="emergencyPhone" placeholder="Enter emergency contact phone" value={formData.emergencyPhone} onChange={handleInputChange} className={formErrors.emergencyPhone ? 'input-error' : ''} />
                {renderError('emergencyPhone')}
              </div>
            </div>
          </section>

          {/* SECTION 9 - UPDATED WITH WORKING UPLOADS */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">9</span>
              <h3>Uploads (Supporting Documents)</h3>
              <p className="section-note">Click "Upload" to select a file. You'll see the file name once uploaded.</p>
            </div>
            <div className="form-grid upload-grid">
              <div className="upload-option">
                <label>
                  <input type="checkbox" name="pressIdUploaded" checked={formData.pressIdUploaded} onChange={handleInputChange} />
                  <span>Professional Press/Media ID <span className="required">(required)</span></span>
                </label>
                <button type="button" className="upload-btn-small" onClick={() => pressIdInputRef.current?.click()} disabled={uploadingFiles.pressId}>
                  {uploadingFiles.pressId ? '⏳' : 'Upload'}
                </button>
                <input type="file" ref={pressIdInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" onChange={handlePressIdUpload} />
                {getUploadStatus('pressId')}
              </div>

              <div className="upload-option">
                <label>
                  <input type="checkbox" name="assignmentLetterUploaded" checked={formData.assignmentLetterUploaded} onChange={handleInputChange} />
                  <span>Assignment Letter / Editorial Authorisation <span className="optional">(if applicable)</span></span>
                </label>
                <button type="button" className="upload-btn-small" onClick={() => assignmentLetterInputRef.current?.click()} disabled={uploadingFiles.assignmentLetter}>
                  {uploadingFiles.assignmentLetter ? '⏳' : 'Upload'}
                </button>
                <input type="file" ref={assignmentLetterInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleAssignmentLetterUpload} />
                {getUploadStatus('assignmentLetter')}
              </div>

              <div className="upload-option">
                <label>
                  <input type="checkbox" name="invitationLetterUploaded" checked={formData.invitationLetterUploaded} onChange={handleInputChange} />
                  <span>AICIS Invitation Letter <span className="optional">(if applicable)</span></span>
                </label>
                <button type="button" className="upload-btn-small" onClick={() => invitationLetterInputRef.current?.click()} disabled={uploadingFiles.invitationLetter}>
                  {uploadingFiles.invitationLetter ? '⏳' : 'Upload'}
                </button>
                <input type="file" ref={invitationLetterInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" onChange={handleInvitationLetterUpload} />
                {getUploadStatus('invitationLetter')}
              </div>
            </div>
          </section>

          {/* SECTION 10 */}
          <section className="form-section">
            <div className="section-header">
              <span className="section-number">10</span>
              <h3>Declaration</h3>
            </div>
            <div className="declaration-box">
              <p>I confirm that the information provided in this form is accurate to the best of my knowledge. I understand that submission does not guarantee accreditation and that only media representatives whose applications have been formally approved will be issued an AICIS 2026 Media Accreditation Pass.</p>
              <p className="declaration-note">Accreditation is subject to security, protocol, venue capacity and operational requirements.</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Applicant Agreement & Signature *</label>
                <input type="text" name="declarationSignature" placeholder="Type your full name as signature" value={formData.declarationSignature} onChange={handleInputChange} className={formErrors.declarationSignature ? 'input-error' : ''} />
                {renderError('declarationSignature')}
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleInputChange} />
              </div>
            </div>
          </section>

          {submitError && (
            <div className="form-error">
              <span className="error-icon">⚠️</span> {submitError}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Media Registration'}
            </button>
          </div>

          <div className="form-footer">
            <div className="footer-contact">
              <h4>📧 AICIS 2026 Media Accreditation Contact</h4>
              <p>Email: <a href="mailto:aicis@abvig.com">aicis@abvig.com</a></p>
              <p><strong>Organiser:</strong> Africa Business Ventures & Investment Group (ABVIG)</p>
              <p><strong>AICIS 2026 Global Summit</strong> | 25–28 November 2026 | Abuja, Nigeria</p>
            </div>
            <div className="footer-note">
              <p><strong>FINAL NOTICE:</strong> Only media representatives whose applications have been formally approved will be issued an AICIS 2026 Media Accreditation Pass. Accreditation is subject to security, protocol, venue capacity and operational requirements.</p>
            </div>
          </div>
        </form>

        <div className="media-registration-back">
          <Link to="/events/global-summit" className="back-button">← Back to Global Summit</Link>
        </div>
      </div>
    </div>
  );
};

export default MediaRegistration;